import React, { useState, useEffect, useRef, useCallback } from "react";
import { UTILS, INS } from "../../config/API";
import { useNavigate } from 'react-router-dom';
import RadioField from "../RadioField";
import CheckboxField from "../CheckboxField";
import SignatureCanvas from "react-signature-canvas";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from 'jspdf';
import ConfirmationModal from "../ConfirmationModal";
import autoTable from "jspdf-autotable";

const TaxInfo = ({ initialData = {}, onDataChange }) => {
    const navigate = useNavigate();
    const [formSections, setFormSections] = useState({
        taxrevenue: { fields: [], title: "Tax Revenue Information", signature: null, signatureText: "", fileUpload: null, fileUploadText: "", fileTypes: "" }
    });
    const [isLoading, setIsLoading] = useState(true);
    const inputRef = useRef(null);
    const isInitialMount = useRef(true);
    const onDataChangeTimeoutRef = useRef(null);

    const [inputValue, setInputValue] = useState("");
    const [emailError, setEmailError] = useState("");
    const [isAPILoading, setIsAPILoading] = useState(false);
    const [formData, setFormData] = useState(initialData);

    const [zipCodes, setZipCodes] = useState([]);
    const [showZipDropdown, setShowZipDropdown] = useState(false);
    const [options, setOptions] = useState([]);
    const [picklistCache, setPicklistCache] = useState({});
    const [signature, setSignature] = useState(null);
    const [files, setFiles] = useState([{ id: 1, file: null, type: "" }]);
    const [showSignature, setShowSignature] = useState(false);
    const [signatureText, setSignatureText] = useState("");
    const [showFileUpload, setShowFileUpload] = useState(false);
    const [fileUploadText, setFileUploadText] = useState("");
    const [isSigned, setIsSigned] = useState(false);
    const [date, setDate] = useState("");
    const [error, setError] = useState(null);
    const sigPad = useRef(null);
    const [fileTypes, setFileTypes] = useState("");
    const [profileData, setProfileData] = useState(null);
    const [taxTypeOptions, setTaxTypeOptions] = useState([]);
    const [taxPayerName, setTaxPayerName] = useState("");

    // Debounced onDataChange function
    const debouncedOnDataChange = useCallback((data) => {
        if (onDataChangeTimeoutRef.current) {
            clearTimeout(onDataChangeTimeoutRef.current);
        }

        onDataChangeTimeoutRef.current = setTimeout(() => {
            if (onDataChange && typeof onDataChange === 'function') {
                onDataChange(data);
            }
        }, 0);
    }, [onDataChange]);

    useEffect(() => {
        const selectedTaxPayer = localStorage.getItem("selectedBusinessName");
        if (selectedTaxPayer) {
            setTaxPayerName(selectedTaxPayer);
        }
    }, []);

    // Single useEffect for handling initialData changes
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            if (initialData && Object.keys(initialData).length > 0) {
                setFormData(initialData);
            }
        } else {
            // Only update if initialData is different from current formData
            if (initialData && JSON.stringify(initialData) !== JSON.stringify(formData)) {
                setFormData(initialData);
            }
        }
    }, [initialData]);

    // Handler for any field change
    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            // Only update if value actually changed
            if (prev[name] === value) {
                return prev;
            }

            const updated = { ...prev, [name]: value };
            debouncedOnDataChange(updated);
            return updated;
        });
    }, [debouncedOnDataChange]);

    // Update formData and notify parent on input change
    const onFieldChange = useCallback((fieldName, value) => {
        setFormData(prev => {
            // Only update if value actually changed
            if (prev[fieldName] === value) {
                return prev;
            }

            const newData = { ...prev, [fieldName]: value };
            debouncedOnDataChange(newData);
            return newData;
        });
    }, [debouncedOnDataChange]);

    useEffect(() => {
        const fetchFormFields = async (itype, stype) => {
            setIsLoading(true);
            try {
                const response = await fetch(UTILS, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        "Accept": "application/json"
                    },
                    body: new URLSearchParams({
                        action: "desc",
                        itype: itype,
                        stype: stype,
                    }),
                });

                const text = await response.text();
                const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");
                console.log(cleanedText);

                let data;
                try {
                    data = JSON.parse(cleanedText);
                } catch (parseError) {
                    console.error("Failed to parse form fields:", parseError);
                    throw new Error("Invalid server response format");
                }

                if (response.ok) {
                    return data;
                } else {
                    throw new Error(data.message || "Failed to fetch form fields");
                }
            } catch (error) {
                console.log(itype)
                console.log(stype)
                console.error("Error loading form:", error);
                setError(error.message);
                return null;
            }
        };

        const fetchDropDownFields = async (itemValueGroup) => {
            if (picklistCache[itemValueGroup]?.data) return;

            setPicklistCache((prev) => ({
                ...prev,
                [itemValueGroup]: { ...prev[itemValueGroup], isLoading: true },
            }));

            try {
                const response = await fetch(UTILS, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        "Accept": "application/json"
                    },
                    body: new URLSearchParams({ action: "idv", s: itemValueGroup || "YesNo" }),
                });

                const text = await response.text();
                const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");
                let data;
                try {
                    data = JSON.parse(cleanedText);
                } catch (parseError) {
                    console.error("Failed to parse dropdown data:", parseError);
                    console.error("Original text:", text);
                    throw new Error("Invalid dropdown data format");
                }

                setPicklistCache((prev) => ({
                    ...prev,
                    [itemValueGroup]: { data, isLoading: false },
                }));
            } catch (error) {
                console.error("Error fetching dropdown:", error);
                setPicklistCache((prev) => ({
                    ...prev,
                    [itemValueGroup]: { isLoading: false, error: error.message },
                }));
            }
        };

        const fetchAndCombineData = async () => {
            try {
                const taxRevenueData = await fetchFormFields("funding", "taxrevenue");

                if (!taxRevenueData) return;

                setFormSections(prev => ({
                    taxrevenue: {
                        ...prev.taxrevenue,
                        fields: taxRevenueData.Fields || [],
                        signature: taxRevenueData.Signature,
                        signatureText: taxRevenueData.SignatureText || "",
                        fileUpload: taxRevenueData.FileUpload,
                        fileUploadText: taxRevenueData.FileUploadText || "",
                        fileTypes: taxRevenueData.FileTypes || ""
                    }
                }));

                const allFields = taxRevenueData.Fields || [];

                const initialFormData = {};
                allFields.forEach((field) => {
                    if (field.defaultvalue) {
                        initialFormData[field.fieldname] = field.defaultvalue;
                    }
                });

                // Only set initial form data if we don't have initialData from parent
                if (!initialData || Object.keys(initialData).length === 0) {
                    setFormData(initialFormData);
                    debouncedOnDataChange(initialFormData);
                }

                const hasSignature = taxRevenueData.Signature;
                const hasFileUpload = taxRevenueData.FileUpload;

                setShowSignature(hasSignature);
                setShowFileUpload(hasFileUpload);

                const picklistFields = allFields.filter(
                    (field) => field.fieldtype === "picklist"
                );
                await Promise.all(
                    picklistFields.map(async (field) => {
                        await fetchDropDownFields(field.itemvaluegroup);
                    })
                );
            } catch (error) {
                console.error("Error in fetchAndCombineData:", error);
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAndCombineData();
    }, []); // Empty dependency array - only run on mount



    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'frc') {
            setInputValue(value);
            onFieldChange(name, value);
        } else if (type === 'checkbox') {
            setFormData((prevFormData) => {
                const currentValues = prevFormData[name] || [];
                let updatedValues;

                if (checked) {
                    updatedValues = [...currentValues, value];
                } else {
                    updatedValues = currentValues.filter((item) => item !== value);
                }

                // Only update if values actually changed
                if (JSON.stringify(currentValues.sort()) === JSON.stringify(updatedValues.sort())) {
                    return prevFormData;
                }

                const updated = {
                    ...prevFormData,
                    [name]: updatedValues,
                };

                debouncedOnDataChange(updated);
                return updated;
            });
        } else {
            onFieldChange(name, value);
        }
    }, [onFieldChange]);

    const handleZipSelection = useCallback((zip) => {
        setInputValue(zip.zipcode);
        onFieldChange('frc', zip.itemid);
        setShowZipDropdown(false);
    }, [onFieldChange]);

    const formatPhoneOnChange = useCallback((e) => {
        const input = e.target;
        let phoneNumber = input.value.replace(/\D/g, '');
        if (phoneNumber.length > 10) {
            phoneNumber = phoneNumber.slice(0, 10);
        }

        let formattedPhone = phoneNumber;
        if (phoneNumber.length > 6) {
            formattedPhone = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
        } else if (phoneNumber.length > 3) {
            formattedPhone = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        } else if (phoneNumber.length > 0) {
            formattedPhone = `(${phoneNumber}`;
        }

        input.value = formattedPhone;
        onFieldChange(input.name, formattedPhone);
    }, [onFieldChange]);

    const formatSSNOnChange = useCallback((e) => {
        const input = e.target.value.replace(/\D/g, "");
        let formattedValue = "";

        if (input.length > 0) {
            formattedValue += input.substring(0, 3);
        }
        if (input.length > 3) {
            formattedValue += "-" + input.substring(3, 5);
        }
        if (input.length > 5) {
            formattedValue += "-" + input.substring(5, 9);
        }

        onFieldChange(e.target.name, formattedValue);
    }, [onFieldChange]);

    const handleCheckboxGroupChange = useCallback((e, fieldName) => {
        const { value, checked } = e.target;
        setFormData(prev => {
            const currentValues = prev[fieldName] || [];
            const updatedValues = checked
                ? [...currentValues, value]
                : currentValues.filter(item => item !== value);

            // Only update if values actually changed
            if (JSON.stringify(currentValues.sort()) === JSON.stringify(updatedValues.sort())) {
                return prev;
            }

            const updated = {
                ...prev,
                [fieldName]: updatedValues
            };

            debouncedOnDataChange(updated);
            return updated;
        });
    }, [debouncedOnDataChange]);

    const handleClear = useCallback(() => {
        if (sigPad.current) {
            sigPad.current.clear();
            setIsSigned(false);
        }
    }, []);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (onDataChangeTimeoutRef.current) {
                clearTimeout(onDataChangeTimeoutRef.current);
            }
        };
    }, []);

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <div className="relative">
                    <div className="w-16 h-16 rounded-full absolute border-4 border-gray-200"></div>
                    <div className="w-16 h-16 rounded-full animate-spin border-4 border-primary border-t-transparent border-r-transparent"></div>

                    <div className="absolute inset-0 flex items-center justify-center">
                        <svg
                            className="w-6 h-6 text-primary"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 14l-7 7m0 0l-7-7m7 7V3"
                            ></path>
                        </svg>
                    </div>
                </div>

                <div className="flex flex-col items-center space-y-2">
                    <p className="text-gray-600 font-medium text-lg animate-pulse">Preparing Your Form...</p>
                    <div className="flex space-x-2">
                        {[...Array(3)].map((_, i) => (
                            <div
                                key={i}
                                className="w-2 h-2 bg-primary rounded-full animate-bounce"
                                style={{ animationDelay: `${i * 0.1}s` }}
                            ></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Form</h2>
                <p className="text-gray-700 mb-4">{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="bg-[#711515] text-white px-6 py-2 rounded-lg hover:bg-[#5a1010] transition"
                >
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="w-[95%] md:w-[80%] p-4 md:p-8 mx-auto bg-white mt-5 rounded-lg">
            {error && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
                    <p>{error}</p>
                </div>
            )}

            {taxPayerName && (
                <p className="text-lg text-gray-600 -mt-6 text-center font-semibold">
                    For: <span className="text-lg  text-[#711515]">{taxPayerName}</span>
                </p>
            )}

            <div className="flex flex-col gap-8">
                {/* Tax Revenue Information */}
                <div key="taxrevenue" className="flex flex-col gap-6">
                    <h3 className="text-xl font-bold text-gray-800 border-b-2 border-[#711515] pb-2">
                        {formSections.taxrevenue.title}
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {formSections.taxrevenue.fields
                            .filter(
                                (field) =>
                                    ![
                                        "itemid",
                                        "autonumber",
                                        "readonlydate",
                                        "readonlypicklist",
                                        "readonly",
                                        "hidden",
                                        "readonlylookup",
                                        "linkedfield",
                                        "inlinequery",
                                        "person",
                                    ].includes(field.fieldtype)
                            )
                            .sort((a, b) => a.sortorder - b.sortorder)
                            .map((field) => (
                                <div className={`${field.fieldtype === "label" ? "col-span-2" : "col-span-1"}`} key={field.displayid}>
                                    <label className={`block ${field.fieldtype === "label" ? "font-bold text-base text-xl w-full" : "text-gray-700 font-semibold"} mb-3`}>
                                        {field.required === "Y" && <span className="text-[#711515]">*</span>} {field.displayname}
                                        {field.tooltip && (
                                            <div className="inline-block relative ml-2 group">
                                                <svg
                                                    className="w-5 h-5 text-gray-400 cursor-help"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                    />
                                                </svg>
                                                <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-64 p-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                                    {field.tooltip}
                                                    <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-2 h-2 bg-gray-800 rotate-45"></div>
                                                </div>
                                            </div>
                                        )}
                                    </label>

                                    {field.fieldtype === "lookup" ? (
                                        <div className="relative">
                                            <input
                                                type="text"
                                                name={field.fieldname}
                                                value={formData[field.fieldname] || ""}
                                                onChange={handleChange}
                                                className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                style={{
                                                    width: '100%'
                                                }}
                                                required={field.required === "Y"}
                                                maxLength={field.maxlength || ""}
                                                autoComplete="off"
                                                ref={inputRef}
                                            />
                                            {showZipDropdown && field.fieldname === 'frc' && (
                                                <div
                                                    className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                                                    style={{ width: inputRef.current ? inputRef.current.offsetWidth : '100%' }}
                                                >
                                                    {zipCodes.length > 0 ? (
                                                        zipCodes.map((zip) => (
                                                            <div
                                                                key={zip.itemid}
                                                                className="p-3 hover:bg-gray-100 cursor-pointer"
                                                                onClick={() => handleZipSelection(zip)}
                                                            >
                                                                {zip.zipcode}
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="p-3 text-gray-500">No results found</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : field.fieldtype === "text" || field.fieldtype === "numeric" || field.fieldtype === "currency" || field.fieldtype === "textdate" ? (
                                        <input
                                            type="text"
                                            name={`${field.fieldname}`}
                                            value={formData[`${field.fieldname}`] || ""}
                                            onChange={handleChange}
                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                            required={field.required === "Y"}
                                            maxLength={field.maxlength || ""}
                                        />
                                    ) : field.fieldtype === "textarea" ? (
                                        <textarea
                                            name={field.fieldname}
                                            value={formData[field.fieldname] || ""}
                                            onChange={handleChange}
                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                            required={field.required === "Y"}
                                            rows="4"
                                        ></textarea>
                                    ) : field.fieldtype === "picklist" ? (
                                        <div className="flex flex-col w-full">
                                            <div className="relative w-full">
                                                <select
                                                    name={field.fieldname}
                                                    value={formData[field.fieldname] || ""}
                                                    onChange={handleChange}
                                                    className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none"
                                                    required={field.required === "Y"}
                                                >
                                                    <option value="">Select {field.displayname}</option>
                                                    {picklistCache[field.itemvaluegroup]?.isLoading ? (
                                                        <option disabled>Loading options...</option>
                                                    ) : (
                                                        (picklistCache[field.itemvaluegroup]?.data || []).map((option) => (
                                                            <option key={option.valueid} value={option.valuetext}>
                                                                {option.valuetext}
                                                            </option>
                                                        ))
                                                    )}
                                                </select>
                                                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                                                    <svg
                                                        className="h-4 w-4"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 20 20"
                                                        fill="currentColor"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                            {picklistCache[field.itemvaluegroup]?.error && (
                                                <p className="text-red-500 text-sm mt-1">
                                                    {picklistCache[field.itemvaluegroup].error}
                                                </p>
                                            )}
                                        </div>
                                    ) : field.fieldtype === "datetimepl" ? (
                                        <div className="w-full">
                                            <DatePicker
                                                selected={formData[field.fieldname] ? new Date(formData[field.fieldname]) : null}
                                                onChange={(date) =>
                                                    onFieldChange(field.fieldname, date.toISOString())
                                                }
                                                showTimeSelect
                                                timeFormat="HH:mm"
                                                timeIntervals={15}
                                                dateFormat="MMMM d, yyyy h:mm aa"
                                                className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                required={field.required === "Y"}
                                                placeholderText="Select date and time"
                                            />
                                        </div>
                                    ) : field.fieldtype === "date" ? (
                                        <input
                                            type="date"
                                            name={field.fieldname}
                                            value={formData[field.fieldname] || ""}
                                            onChange={handleChange}
                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                            required={field.required === "Y"}
                                        />
                                    ) : field.fieldtype === "checkbox" ? (
                                        <CheckboxField
                                            field={field}
                                            formData={formData}
                                            handleCheckboxGroupChange={handleCheckboxGroupChange}
                                            picklistCache={picklistCache}
                                            setPicklistCache={setPicklistCache}
                                        />
                                    ) : field.fieldtype === "radio" ? (
                                        <RadioField
                                            field={field}
                                            formData={formData}
                                            handleChange={handleChange}
                                            picklistCache={picklistCache}
                                            setPicklistCache={setPicklistCache}
                                        />
                                    ) : null}
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaxInfo;
